import { FC } from 'react'
import {
  Edit,
  required,
  SimpleForm,
  TextInput,
  SelectInput,
  useRecordContext,
} from 'react-admin'
import { ActionsToolbar, ConfirmDeleteToolbar, HeaderTitle } from '../../components'
import { CertificateOut, CertificateType, CertificateUpdate, TargetSystem } from '../../types/apiRequests'
import { CERTIFICATE_WRITE_SCOPE } from '../../config'
import { COMMON_NAME_EDITABLE_STATUSES, getCapabilities } from './targetSystems'
import { hostnameValidation } from '../../utils/validation'
import { getCommonName } from '../../utils/getCommonName'

// Hostname is user-owned for manual systems, until the CSR is downloaded.
// Mirrors the API: a missing hostname is always editable, to repair legacy records.
const canEditCommonName = (record?: CertificateOut): boolean => {
  if (!record || getCapabilities(record.targetSystem)?.commonName !== 'manual') return false

  const hasEditableStatus = COMMON_NAME_EDITABLE_STATUSES.includes(record.status)
  const hasMissingHostname = !getCommonName(record)
  return hasEditableStatus || hasMissingHostname
}

// PUT only editable fields; targetSystem/type are fixed after create.
// metaData is replaced as a whole by the API, so keep its other keys.
const transform = (data: CertificateOut, options?: { previousData?: CertificateOut }): CertificateUpdate => {
  const update: CertificateUpdate = {
    contactNaam: data.contactNaam,
    contactEmail: data.contactEmail,
    contactTelefoon: data.contactTelefoon,
  }
  if (canEditCommonName(options?.previousData)) {
    update.metaData = {
      ...options?.previousData?.metaData,
      commonName: (data.metaData as { commonName?: string } | null)?.commonName,
    }
  }
  return update
}

const CommonNameInput: FC = () => {
  const record = useRecordContext<CertificateOut>()
  if (!canEditCommonName(record)) return null
  return (
    <TextInput
      source="metaData.commonName"
      label="resources.certificates.fields.host"
      validate={[required(), ...hostnameValidation]}
      fullWidth
    />
  )
}

const CertificateEdit: FC = () => (
  <Edit
    actions={<ActionsToolbar i18n="resources.certificates.edit" />}
    title={<HeaderTitle i18n="resources.certificates.edit" />}
    redirect="show"
    transform={transform}
  >
    <SimpleForm toolbar={<ConfirmDeleteToolbar access={[CERTIFICATE_WRITE_SCOPE]} />} >
      <SelectInput
        source="targetSystem"
        label="resources.certificates.fields.targetSystem"
        choices={Object.values(TargetSystem).map((value) => ({
          id: value,
          name: `resources.certificates.targetSystems.${value.toLowerCase()}`
        }))}
        readOnly
        fullWidth
      />

      <TextInput source="contactNaam" label="data.contactpersoon" validate={[required()]} fullWidth />
      <TextInput source="contactEmail" type="email" label="data.email" validate={[required()]} fullWidth />
      <TextInput source="contactTelefoon" label="data.phone" validate={[required()]} fullWidth />

      <SelectInput
        source="type"
        label="resources.certificates.fields.type"
        choices={Object.values(CertificateType).map((value) => ({
          id: value,
          name: `resources.certificates.types.${value.toLowerCase()}`
        }))}
        readOnly
        fullWidth
      />

      <CommonNameInput />
    </SimpleForm>
  </Edit>
)

export default CertificateEdit
